import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";
import VLFModal from "@/components/Modal";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";

import {ModuleAPI} from "@/api/module";
import {PlayerRoleAPI} from "@/api/player_role";

import {
	AREA_LABELS,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";

function SettingsModulePage(props) {
	const {handleNotification} = props;
	const {id} = useParams();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [moduleData, setModuleData] = useState({});

	const [allPlayerRoles, setAllPlayerRoles] = useState([]);
	const [rolesLoading, setRolesLoading] = useState(false);

	const getModule = async () => {
		setLoading(true);
		try {
			const response = await ModuleAPI.getById(id);
			setModuleData(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const loadPlayerRoles = async () => {
		try {
			const response = await PlayerRoleAPI.list();
			setAllPlayerRoles(response || []);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	useEffect(() => {
		getModule();
		loadPlayerRoles();
	}, []);

	const deleteModule = async () => {
		setLoading(true);
		try {
			await ModuleAPI.delete(id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/settings/modules");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const assignedRoleIds = (moduleData?.roles || []).map((r) => r.playerRoleId);

	const addRoleAssignment = async (playerRoleId) => {
		setRolesLoading(true);
		try {
			await ModuleAPI.addRole(id, playerRoleId);
			await getModule();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setRolesLoading(false);
		}
	};

	const removeRoleAssignment = async (playerRoleId) => {
		setRolesLoading(true);
		try {
			await ModuleAPI.removeRole(id, playerRoleId);
			await getModule();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setRolesLoading(false);
		}
	};

	const mainConfiguration = [
		{value: moduleData?.name, label: "Nome", size: "col-sm-12 col-lg-2"},
		{
			value: moduleData?.defenders,
			label: "Difensori",
			size: "col-sm-6 col-lg-2",
		},
		{
			value: moduleData?.defensiveMidfielders,
			label: "Mediani",
			size: "col-sm-6 col-lg-2",
		},
		{
			value: moduleData?.centralMidfielders,
			label: "Centrocampisti",
			size: "col-sm-6 col-lg-2",
		},
		{
			value: moduleData?.attackingMidfielders,
			label: "Trequartista",
			size: "col-sm-6 col-lg-2",
		},
		{
			value: moduleData?.attackers,
			label: "Attaccanti",
			size: "col-sm-6 col-lg-2",
		},
	];

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate("/settings/modules"),
			color: "info",
		},
		{
			icon: <EditIcon />,
			title: "Modifica",
			onClick: () => navigate("/settings/modules/" + moduleData?.id + "/edit"),
		},
		{
			icon: <DeleteIcon />,
			title: "Elimina",
			onClick: () => setOpenHandleConfirm(true),
			color: "error",
		},
	];

	const breadcrumbsElements = [
		{label: "Moduli tattici", path: "/settings/modules"},
		{label: "/"},
	];

	const availableRoles = allPlayerRoles.filter(
		(r) => !assignedRoleIds.includes(r.id),
	);

	// Raggruppa i ruoli assegnati per area
	const AREA_ORDER = [
		"Goalkeeper",
		"Defense",
		"DefensiveMidfield",
		"Midfield",
		"AttackingMidfield",
		"Attack",
	];
	const rolesByArea = AREA_ORDER.reduce((acc, area) => {
		const roles = (moduleData?.roles || []).filter(
			(r) => r.playerRole?.area === area,
		);
		if (roles.length) acc[area] = roles;
		return acc;
	}, {});
	// Ruoli senza area (assegnati)
	const rolesWithoutArea = (moduleData?.roles || []).filter(
		(r) => !r.playerRole?.area,
	);

	// Raggruppa i ruoli disponibili per area
	const availableByArea = AREA_ORDER.reduce((acc, area) => {
		const roles = availableRoles.filter((r) => r.area === area);
		if (roles.length) acc[area] = roles;
		return acc;
	}, {});
	const availableWithoutArea = availableRoles.filter((r) => !r.area);

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={moduleData ? moduleData.name || "" : ""}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="row g-2 no-margin">
				<div className="col-sm-12 padding-small">
					<VLFCard
						title="Informazioni principali"
						content={
							<div className="row">
								<div className="col-sm-12">
									<ViewField configuration={mainConfiguration} />
								</div>
							</div>
						}
						openable={false}
					/>
				</div>
				<div className="col-sm-12 padding-small">
					<VLFCard
						title="Ruoli assegnati"
						content={
							<div>
								{/* RUOLI SELEZIONATI */}
								<div className="col-sm-12 padding-small">
									<div
										className="list-title"
										style={{fontWeight: 600, marginBottom: 8}}
									>
										Ruoli selezionati
									</div>
									{rolesLoading ? (
										<Loading />
									) : (moduleData?.roles || []).length === 0 ? (
										<div className="text-muted" style={{fontSize: 13}}>
											Nessun ruolo assegnato
										</div>
									) : (
										<div className="row no-margin">
											{Object.entries(rolesByArea).map(([area, roles]) => (
												<div
													key={area}
													className="col-sm-12 col-md-6 col-lg-3 padding-small"
												>
													<div
														style={{
															fontWeight: 600,
															marginBottom: 6,
															fontSize: 13,
														}}
													>
														{AREA_LABELS[area] || area}
													</div>
													<div className="d-flex flex-column gap-1">
														{roles.map((r) => (
															<div
																key={r.playerRoleId}
																className="d-flex align-items-center justify-content-between px-2 py-1 rounded"
																style={{
																	background:
																		"var(--primary-alpha-10, #e8f0fe)",
																	border:
																		"1px solid var(--primary-color, #1a73e8)",
																	fontSize: 13,
																}}
															>
																<span>
																	{r.playerRole?.name || r.playerRoleId}
																</span>
																<CloseIcon
																	fontSize="small"
																	style={{cursor: "pointer", marginLeft: 4}}
																	onClick={() =>
																		removeRoleAssignment(r.playerRoleId)
																	}
																/>
															</div>
														))}
													</div>
												</div>
											))}
											{rolesWithoutArea.length > 0 && (
												<div className="col-sm-12 col-md-6 col-lg-3 padding-small">
													<div
														style={{
															fontWeight: 600,
															marginBottom: 6,
															fontSize: 13,
														}}
													>
														Altro
													</div>
													<div className="d-flex flex-column gap-1">
														{rolesWithoutArea.map((r) => (
															<div
																key={r.playerRoleId}
																className="d-flex align-items-center justify-content-between px-2 py-1 rounded"
																style={{
																	background:
																		"var(--primary-alpha-10, #e8f0fe)",
																	border:
																		"1px solid var(--primary-color, #1a73e8)",
																	fontSize: 13,
																}}
															>
																<span>
																	{r.playerRole?.name || r.playerRoleId}
																</span>
																<CloseIcon
																	fontSize="small"
																	style={{cursor: "pointer", marginLeft: 4}}
																	onClick={() =>
																		removeRoleAssignment(r.playerRoleId)
																	}
																/>
															</div>
														))}
													</div>
												</div>
											)}
										</div>
									)}
								</div>

								{/* RUOLI DISPONIBILI */}
								<div
									className="col-sm-12 padding-small"
									style={{marginTop: 16}}
								>
									<div
										className="list-title"
										style={{fontWeight: 600, marginBottom: 8}}
									>
										Seleziona ruoli
									</div>
									{availableRoles.length === 0 ? (
										<div className="text-muted" style={{fontSize: 13}}>
											Tutti i ruoli sono stati assegnati
										</div>
									) : (
										<div className="row no-margin">
											{Object.entries(availableByArea).map(([area, roles]) => (
												<div
													key={area}
													className="col-sm-12 col-md-6 col-lg-3 padding-small"
												>
													<div
														style={{
															fontWeight: 600,
															marginBottom: 6,
															fontSize: 13,
														}}
													>
														{AREA_LABELS[area] || area}
													</div>
													<div className="d-flex flex-column gap-1">
														{roles.map((r) => (
															<div
																key={r.id}
																className="d-flex align-items-center justify-content-between px-2 py-1 rounded"
																style={{
																	background:
																		"var(--background-secondary, #f5f5f5)",
																	border: "1px solid var(--border-color, #ddd)",
																	fontSize: 13,
																	cursor: rolesLoading
																		? "not-allowed"
																		: "pointer",
																	opacity: rolesLoading ? 0.5 : 1,
																}}
																onClick={() =>
																	!rolesLoading && addRoleAssignment(r.id)
																}
															>
																<span>{r.name}</span>
																<AddIcon
																	fontSize="small"
																	style={{marginLeft: 4}}
																/>
															</div>
														))}
													</div>
												</div>
											))}
											{availableWithoutArea.length > 0 && (
												<div className="col-sm-12 col-md-6 col-lg-3 padding-small">
													<div
														style={{
															fontWeight: 600,
															marginBottom: 6,
															fontSize: 13,
														}}
													>
														Altro
													</div>
													<div className="d-flex flex-column gap-1">
														{availableWithoutArea.map((r) => (
															<div
																key={r.id}
																className="d-flex align-items-center justify-content-between px-2 py-1 rounded"
																style={{
																	background:
																		"var(--background-secondary, #f5f5f5)",
																	border: "1px solid var(--border-color, #ddd)",
																	fontSize: 13,
																	cursor: rolesLoading
																		? "not-allowed"
																		: "pointer",
																	opacity: rolesLoading ? 0.5 : 1,
																}}
																onClick={() =>
																	!rolesLoading && addRoleAssignment(r.id)
																}
															>
																<span>{r.name}</span>
																<AddIcon
																	fontSize="small"
																	style={{marginLeft: 4}}
																/>
															</div>
														))}
													</div>
												</div>
											)}
										</div>
									)}
								</div>
							</div>
						}
						openable={false}
					/>
				</div>
			</div>
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={() => deleteModule()}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>
		</Page>
	);
}

export default SettingsModulePage;
